import { DisposableCollection } from '../../misc/DisposableCollection/DisposableCollection';
import { Disposable } from '../../utils/DisposableContainer/DisposableContainer';
import { GeolocationManagerStrategy } from './strategy/GeolocationManagerStrategy';
export class GeolocationManager {
  private readonly bindingInfoCollection = new DisposableCollection<BindingInfo>();
  private watchId: number | null = null;
  private lastLocationInfo: LocationInfo = {
    type: LocationInfoType.Unavailable
  };
  constructor(private readonly strategy: GeolocationManagerStrategy) {}
  public bindGeolocation(geolocationUpdated: (locationInfo: LocationInfo) => void): Disposable {
    return this.registerBindingInfo({
      geolocationUpdated,
      requiresWatcher: true
    });
  }
  public bindOptionalGeolocation(geolocationUpdated: (locationInfo: LocationInfo) => void): Disposable {
    return this.registerBindingInfo({
      geolocationUpdated,
      requiresWatcher: false
    });
  }
  private registerBindingInfo(bindingInfo: BindingInfo): Disposable {
    const callbackDisposable = this.bindingInfoCollection.add(bindingInfo);
    if (this.watchId == null && this.watcherRequired()) {
      this.startWatchingGeoLocation();
      // call all callbacks here, so callbacks without requiresWatcher also get informed that we started watching
      this.callGeolocationUpdatedCallbacksWithLastInfo();
    } else {
      bindingInfo.geolocationUpdated(this.lastLocationInfo);
    }
    return {
      dispose: () => {
        callbackDisposable.dispose();
        if (!this.watcherRequired()) {
          this.stopWatchingGeoLocation();

          // call all remaining callbacks without requiresWatcher so that they know there isn't position data available anymore
          this.callGeolocationUpdatedCallbacksWithLastInfo();
        }
      }
    };
  }
  private startWatchingGeoLocation(): void {
    this.lastLocationInfo = {
      type: LocationInfoType.Pending
    };
    this.watchId = this.strategy.watchPosition(position => {
      this.lastLocationInfo = {
        type: LocationInfoType.Available,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };
      this.callGeolocationUpdatedCallbacksWithLastInfo();
    }, error => {
      switch (error.code) {
        case GeolocationPositionError.PERMISSION_DENIED:
          this.lastLocationInfo = {
            type: LocationInfoType.NoPermission
          };
          break;
        case GeolocationPositionError.TIMEOUT:
          this.lastLocationInfo = {
            type: LocationInfoType.Unavailable
          };
          break;
        case GeolocationPositionError.POSITION_UNAVAILABLE:
          this.lastLocationInfo = {
            type: LocationInfoType.Unavailable
          };
          break;
        default:
          throw new Error(`unknown position error code "${error.code}"`);
      }
      this.callGeolocationUpdatedCallbacksWithLastInfo();
    });
  }
  private callGeolocationUpdatedCallbacksWithLastInfo(): void {
    for (const info of this.bindingInfoCollection.toArray()) {
      info.geolocationUpdated(this.lastLocationInfo);
    }
  }
  private stopWatchingGeoLocation(): void {
    if (this.watchId != null) {
      this.strategy.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.lastLocationInfo = {
      type: LocationInfoType.Unavailable
    };
  }
  private watcherRequired(): boolean {
    return this.bindingInfoCollection.toArray().some(info => info.requiresWatcher);
  }
}
export enum LocationInfoType {
  Pending = 'pending',
  Available = 'available',
  NoPermission = 'no_permission',
  Unavailable = 'unavailable',
}
type BaseLocationInfo<T extends {
  type: LocationInfoType;
}> = T;
export type PendingLocationInfo = BaseLocationInfo<{
  type: LocationInfoType.Pending;
}>;
export type NoPermissionLocationInfo = BaseLocationInfo<{
  type: LocationInfoType.NoPermission;
}>;
export type UnavailableLocationInfo = BaseLocationInfo<{
  type: LocationInfoType.Unavailable;
}>;
export type AvailableLocationInfo = BaseLocationInfo<{
  type: LocationInfoType.Available;
  latitude: number;
  longitude: number;
}>;
export type LocationInfo = PendingLocationInfo | NoPermissionLocationInfo | UnavailableLocationInfo | AvailableLocationInfo;
type BindingInfo = {
  geolocationUpdated: (geoLocation: LocationInfo) => void;
  /**
   * if this is true, then this binding will start/stop the watching of the geolocation
   * if this is false, this has no effect for watching the geolocation
   */
  requiresWatcher: boolean;
};